// Modal to create or fully replace a project template (with its subtasks).
// PUT /project-templates/:id is a full-replace (subtasks too) so the UI lets
// you reorder / add / remove rows without thinking about endpoint granularity.
//
// Each subtask row: title, description (collapsed by default), priority,
// offset_days (negative = before anchor, positive = after), owners (RaciPicker).

import { useState } from 'react';
import * as api from '../lib/api.ts';
import { RaciPicker } from './RaciPicker.jsx';

export const TemplateEditModal = ({ template, groups = [], staff = [], onClose, onSaved }) => {
  const isUpdate = !!template;
  const [id, setId] = useState(template?.id || '');
  const [name, setName] = useState(template?.name || '');
  const [description, setDescription] = useState(template?.description || '');
  const [groupId, setGroupId] = useState(template?.groupId || '');
  const [subtasks, setSubtasks] = useState(() => {
    const initial = template?.subtasks ?? [];
    return initial.length > 0
      ? initial.map(s => ({
          ord: s.ord,
          title: s.title,
          description: s.description || '',
          priority: s.priority || 'P1',
          offset_days: s.offsetDays ?? 0,
          owners: s.owners || [],
          _open: false
        }))
      : [makeBlankSub(1)];
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  function makeBlankSub(ord) {
    return { ord, title: '', description: '', priority: 'P1', offset_days: 0, owners: [], _open: true };
  }

  function addSub() {
    const nextOrd = Math.max(0, ...subtasks.map(s => s.ord)) + 1;
    setSubtasks([...subtasks, makeBlankSub(nextOrd)]);
  }
  function removeSub(i) {
    setSubtasks(subtasks.filter((_, idx) => idx !== i));
  }
  function patchSub(i, patch) {
    setSubtasks(subtasks.map((s, idx) => idx === i ? { ...s, ...patch } : s));
  }
  function moveSub(i, dir) {
    const j = i + dir;
    if (j < 0 || j >= subtasks.length) return;
    const next = [...subtasks];
    [next[i], next[j]] = [next[j], next[i]];
    // Re-sequence ord to match new order
    setSubtasks(next.map((s, idx) => ({ ...s, ord: idx + 1 })));
  }

  async function save() {
    if (!id.trim() || !name.trim()) {
      setErr('id 和 name 必填');
      return;
    }
    if (subtasks.length === 0) {
      setErr('至少要有一个子项模板');
      return;
    }
    for (const s of subtasks) {
      if (!s.title.trim()) {
        setErr('每个子项都需要标题');
        return;
      }
    }
    setSaving(true);
    setErr('');
    const payload = {
      id: id.trim(),
      name: name.trim(),
      description: description.trim() || undefined,
      group_id: groupId || undefined,
      subtasks: subtasks.map((s, idx) => ({
        ord: idx + 1,
        title: s.title.trim(),
        description: s.description.trim() || null,
        priority: s.priority,
        offset_days: Number(s.offset_days) || 0,
        owners: s.owners
      }))
    };
    try {
      if (isUpdate) {
        await api.updateTemplate(id.trim(), {
          name: payload.name, description: payload.description,
          group_id: payload.group_id, subtasks: payload.subtasks
        });
      } else {
        await api.createTemplate(payload);
      }
      onSaved?.();
      onClose();
    } catch (e) {
      setErr(e.message || '保存失败');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="vava-modal-backdrop" onClick={onClose}>
      <div className="vava-modal tmpl-edit-modal" onClick={(e) => e.stopPropagation()}>
        <div className="vava-modal-head">
          <h2>{isUpdate ? `编辑模板 · ${template.name}` : '新建项目模板'}</h2>
          <button className="vava-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="vava-modal-body">
          <div className="vava-field">
            <label className="vava-label">模板 ID *</label>
            <input className="vava-input" value={id} onChange={(e) => setId(e.target.value)}
                   disabled={isUpdate} placeholder="PT002" />
          </div>
          <div className="vava-field">
            <label className="vava-label">名称 *</label>
            <input className="vava-input" value={name} onChange={(e) => setName(e.target.value)}
                   placeholder="如：招聘流程" />
          </div>
          <div className="vava-field">
            <label className="vava-label">默认分类（生成的待办会归到此分类）</label>
            <select className="vava-select" value={groupId || ''}
                    onChange={(e) => setGroupId(e.target.value)}>
              <option value="">—</option>
              {groups.filter(g => !g.archived)
                .sort((a, b) => a.displayOrder - b.displayOrder)
                .map(g => (
                  <option key={g.id} value={g.id}>
                    {g.icon ? g.icon + ' ' : ''}{g.parentId ? '— ' : ''}{g.name}
                  </option>
                ))}
            </select>
          </div>
          <div className="vava-field">
            <label className="vava-label">描述</label>
            <textarea className="vava-textarea" rows={2}
                      value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>

          <div className="tmpl-subs-header">
            <span className="vava-label">子项模板 *</span>
            <span style={{ fontSize: 11, color: 'var(--vava-text-subtle)' }}>
              偏移天数 0 = 锚定日期当天，负数 = 之前，正数 = 之后
            </span>
          </div>

          {subtasks.map((s, i) => (
            <div key={i} className="tmpl-sub-row">
              <div className="tmpl-sub-line1">
                <div className="tmpl-sub-handle">
                  <button onClick={() => moveSub(i, -1)} disabled={i === 0} aria-label="上移">↑</button>
                  <button onClick={() => moveSub(i, 1)} disabled={i === subtasks.length - 1} aria-label="下移">↓</button>
                </div>
                <span className="tmpl-sub-ord mono">#{i + 1}</span>
                <input className="vava-input" placeholder="子项标题" value={s.title}
                       onChange={(e) => patchSub(i, { title: e.target.value })}
                       style={{ flex: 1 }} />
                <input className="vava-input mono" type="number" value={s.offset_days}
                       onChange={(e) => patchSub(i, { offset_days: e.target.value })}
                       style={{ width: 70 }} aria-label="偏移天数" />
                <span style={{ fontSize: 11, color: 'var(--vava-text-subtle)' }}>d</span>
                <select className="vava-select" value={s.priority}
                        onChange={(e) => patchSub(i, { priority: e.target.value })}
                        style={{ width: 64 }}>
                  <option value="P0">P0</option>
                  <option value="P1">P1</option>
                  <option value="P2">P2</option>
                </select>
                <button className="raci-edit-btn"
                        onClick={() => patchSub(i, { _open: !s._open })}>
                  {s._open ? '收起' : '展开'}
                </button>
                <button className="raci-edit-btn" style={{ color: 'var(--vava-red)' }}
                        onClick={() => removeSub(i)}>删除</button>
              </div>
              {s._open && (
                <div className="tmpl-sub-line2">
                  <div className="vava-field" style={{ marginBottom: 8 }}>
                    <label className="vava-label">描述（可选）</label>
                    <textarea className="vava-textarea" rows={2} value={s.description}
                              onChange={(e) => patchSub(i, { description: e.target.value })} />
                  </div>
                  <div className="vava-field" style={{ marginBottom: 0 }}>
                    <label className="vava-label">负责人 · RACI</label>
                    <RaciPicker owners={s.owners} staff={staff}
                                onChange={(o) => patchSub(i, { owners: o })} />
                  </div>
                </div>
              )}
            </div>
          ))}

          <button className="btn btn-secondary" onClick={addSub} style={{ marginTop: 8 }}>
            + 加子项模板
          </button>

          {err && <div className="att-error" role="alert" style={{ marginTop: 12 }}>{err}</div>}
        </div>
        <div className="vava-modal-foot">
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>取消</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? '保存中…' : (isUpdate ? '保存修改' : '创建模板')}
          </button>
        </div>
      </div>
    </div>
  );
};
